import { useMutation, useQuery } from "@apollo/client";
import {
  DeleteInboxMessageClassificationDocument,
  InboxMessageClassificationsDocument,
  UpdateInboxMessageClassificationDocument,
  useMyAccountQuery,
  useMyInvokablePlaybooksQuery,
} from "@qw/graphql";
import { AUTO_RUN_PLAYBOOK_ALLOWLIST } from "@qw/qw-common";
import {
  Button,
  Column,
  DropdownMenuItem,
  Error,
  Grid,
  GridCell,
  GridEmpty,
  GridRow,
  GridRowHeader,
  Icon,
  MoreMenu,
  Spinner,
  useModal,
  useToast,
} from "@qw/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import { ClassificationForm, createClassificationValidationSchema } from "./classification-form";

interface Classification {
  id: string;
  label: string;
  description: string;
  source: "System" | "User";
  associatedPlaybookId?: string | null;
  playbook?: { id: string; name: string } | null;
  autoRunPlaybook?: boolean;
}

export const ClassificationsPage = () => {
  const { data: me } = useMyAccountQuery();
  const myEmail = me?.myAccount?.email?.toLowerCase() ?? "";
  // The inbox LD provider is initialized anonymously (no user email in context),
  // so the flag evaluates to its default-off value for everyone here. OR-ing with
  // the allowlist keeps today's allowlisted users enabled regardless of LD state
  // (LD only ever *adds*), matching the API's `default = allowlist.includes(email)`
  // semantics. Once the app identifies the user in LD, the flag can target by email.
  const flags = useFlags<Record<string, boolean | undefined>>();
  const canAutoRunPlaybook =
    AUTO_RUN_PLAYBOOK_ALLOWLIST.includes(myEmail) || flags["enable-classification-autorun"] === true;
  const { data, loading, error } = useQuery<{ inboxMessageClassifications: Classification[] }>(
    InboxMessageClassificationsDocument
  );
  // Classifications auto-run a playbook at the top level with no parent
  // orchestrator context. myInvokablePlaybooks returns owned + subscribed
  // filtered to type=Workflow server-side, so the picker only ever offers
  // playbooks the classification fire-path can actually dispatch.
  const { data: invokablePlaybooksData } = useMyInvokablePlaybooksQuery();

  const [updateMutation, { loading: updating }] = useMutation(UpdateInboxMessageClassificationDocument, {
    refetchQueries: ["InboxMessageClassifications"],
  });

  const [deleteMutation, { loading: deleting }] = useMutation(DeleteInboxMessageClassificationDocument, {
    refetchQueries: ["InboxMessageClassifications"],
  });

  const { openForm, openConfirmation } = useModal();

  const { systemClassifications, userClassifications } = useMemo(() => {
    const list = data?.inboxMessageClassifications ?? [];
    return {
      systemClassifications: list.filter((c) => c.source === "System"),
      userClassifications: list.filter((c) => c.source === "User"),
    };
  }, [data]);

  const playbookOptions = useMemo(() => {
    return (invokablePlaybooksData?.myInvokablePlaybooks ?? [])
      .map((p) => ({ id: p.id, name: p.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [invokablePlaybooksData]);

  const { toast } = useToast();

  const openEditForm = (c: Classification) => {
    openForm(
      <ClassificationForm
        mode="edit"
        initial={{
          id: c.id,
          label: c.label,
          description: c.description,
          associatedPlaybookId: c.associatedPlaybookId ?? null,
          autoRunPlaybook: c.autoRunPlaybook ?? false,
        }}
        playbookOptions={playbookOptions}
        submitLabel="Save"
        disabled={updating}
        canAutoRunPlaybook={canAutoRunPlaybook}
        userClassifications={userClassifications}
        renderFieldsOnly
        onSubmit={() => {}}
      />,
      {
        title: "Edit Classification",
        type: "dialog",
        validationSchema: createClassificationValidationSchema("edit", userClassifications, c.id),
        defaultValues: {
          label: c.label,
          description: c.description,
          associatedPlaybookId: c.associatedPlaybookId ?? "__none__",
          autoRunPlaybook: c.autoRunPlaybook ?? false,
        },
        onSubmit: async (values, close) => {
          const normalized = {
            id: c.id,
            label: values.label,
            description: values.description ?? "",
            associatedPlaybookId: values.associatedPlaybookId === "__none__" ? null : values.associatedPlaybookId,
            autoRunPlaybook: canAutoRunPlaybook ? Boolean(values.autoRunPlaybook) : undefined,
          };

          await updateMutation({
            variables: {
              input: normalized,
            },
          });
          close();
          toast({ title: `Classification updated` });
        },
      }
    );
  };

  const onDelete = async (classification: Classification) => {
    const confirmed = await openConfirmation({
      title: `Delete "${classification.label}"`,
      description: (
        <>
          Are you sure you want to delete this classification?{" "}
          <strong className="font-medium text-destructive">This action cannot be undone.</strong>
        </>
      ),
      type: "alert",
      buttonProps: {
        children: "Delete",
        theme: "destructive",
        disabled: deleting,
      },
    });

    if (confirmed) {
      await deleteMutation({ variables: { id: classification.id } });
      toast({ title: `Classification deleted` });
    }
  };

  if (loading) {
    return (
      <Column className="flex flex-col items-center justify-center w-full h-full">
        <Spinner />
        <h2 className="mt-2">Loading classifications</h2>
      </Column>
    );
  }
  if (error) {
    return <Error error={error} className="p-4 sm:p-6 md:p-8" />;
  }

  return (
    <Column className="flex-auto">
      <h3 className="mt-4 px-6 py-2 font-medium">My classifications</h3>
      {userClassifications[0] ? (
        <Grid className="grid-cols-[1fr_3fr_1fr_1.5rem]">
          <GridRowHeader className="pt-4">
            <GridCell>Label</GridCell>
            <GridCell>Description</GridCell>
            <GridCell>Playbook</GridCell>
          </GridRowHeader>

          {userClassifications.map((c) => (
            <GridRow key={c.id} className="items-baseline">
              <GridCell className="font-medium">{c.label}</GridCell>
              <GridCell className="text-wrap py-4 max-w-6xl text-muted-foreground">{c.description}</GridCell>
              <GridCell className="text-wrap">{c.playbook ? c.playbook.name : "None"}</GridCell>
              <GridCell className="justify-self-end">
                <MoreMenu>
                  <DropdownMenuItem asChild>
                    <Button
                      onClick={() => openEditForm(c)}
                      size="sm"
                      variant="ghost"
                      className="w-full justify-start cursor-pointer"
                    >
                      <Icon icon="edit" />
                      Edit
                    </Button>
                  </DropdownMenuItem>

                  <DropdownMenuItem variant="destructive" asChild>
                    <Button
                      onClick={() => onDelete(c)}
                      disabled={deleting}
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="w-full justify-start cursor-pointer"
                    >
                      <Icon icon="delete" />
                      Delete
                    </Button>
                  </DropdownMenuItem>
                </MoreMenu>
              </GridCell>
            </GridRow>
          ))}
        </Grid>
      ) : (
        <Grid>
          <GridEmpty
            icon="label"
            title="No classifications yet"
            description="Create a classification to get started."
          />
        </Grid>
      )}

      <div className="mt-6">
        <h3 className="font-medium px-6 py-2">System classifications</h3>
        {systemClassifications[0] ? (
          <Grid className="grid-cols-[1fr_3fr_1fr_1.5rem]">
            <GridRowHeader className="pt-4">
              <GridCell>Label</GridCell>
              <GridCell>Description</GridCell>
            </GridRowHeader>
            {systemClassifications.map((c: Classification) => (
              <GridRow key={c.id}>
                <GridCell>
                  <div className="font-medium">{c.label}</div>
                </GridCell>
                <GridCell className="text-wrap py-4 max-w-6xl text-muted-foreground">{c.description}</GridCell>
              </GridRow>
            ))}
          </Grid>
        ) : (
          <Grid>
            <GridEmpty icon="info" title="No system classifications" />
          </Grid>
        )}
      </div>
    </Column>
  );
};
